import { React, useState, useEffect } from 'react';
import '../../App.css'
import { Grid, Box, Button, Paper, FormControl, InputLabel, Select, MenuItem, FormHelperText, Typography, CircularProgress } from '@mui/material'; // Added CircularProgress
import { RequestQuote } from '@mui/icons-material';
import * as monthList from '../../services/monthList';
import * as yearList from '../../services/yearList';
import Popup from '../Popup';
import { HiOutlineCurrencyRupee } from "react-icons/hi2";
import DataTable from '../DataTable';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import Notification from '../Notification';
import { setLoaderMessage } from '../../utils/loaderManager';

// Custom component for required field label
const RequiredLabel = ({ label }) => (
  <span>
    {label}
    <span style={{ color: 'red', marginLeft: '3px' }}>*</span>
  </span>
);

const monthOptions = monthList.getMonthList();
const yearOptions = yearList.getYearList();

const today = new Date();
const currentYear = today.getFullYear().toString();
const currentMonthIndex = today.getMonth();
const currentMonthName = monthOptions[currentMonthIndex].title;

const initialFValues = {
  id: 0,
  month: currentMonthName,
  year: currentYear,
}

function CombineSalaryReport({ isEmbedded = false }) {

  const [empDetails, setEmpDetails] = useState([]);
  const [values, setValues] = useState(initialFValues);
  const [errors, setErrors] = useState({});
  const [openPopup, setOpenPopup] = useState(false);
  const navigate = useNavigate();
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const [loading, setLoading] = useState(false); // New loading state

  const validate = () => {
    let temp = {}
    // Note: In the original code, you used month.length != 0 and year.length != 0, 
    // which will always be true since they are initialized. 
    // Assuming you want to check for non-empty string *after* user interaction.
    temp.month = values.month ? "" : "This field is required."
    temp.year = values.year ? "" : "This field is required."

    setErrors({
      ...temp
    })

    return Object.values(temp).every(x => x == "")
  }

  function handleInputChange(e) {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value
    })
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ""
      });
    }
  }

  // Removed the original handleSubmit function

  const handleDisplayClick = (e) => {
    e.preventDefault();

    if (validate()) {
      setLoading(true); // Start loading
      setLoaderMessage('Generating salary report...');

      axios.post(`${process.env.REACT_APP_API_BASE_URL}/report/comb-sal-report-details`, values)
        .then((response) => {
          setEmpDetails(response.data);

          // Navigate to the report page with data upon success
          navigate('/combined-salary-report', { state: { empDetails: response.data, month: values.month, year: values.year } });

        })
        .catch((error) => {
          const status = error.response ? error.response.status : null;
          if (status === 401) {
            setNotify({
              isOpen: true,
              message: 'Session Expired!',
              type: 'error'
            });
            setTimeout(() => {
              navigate('/');
            }, 1000);
          } else {
            // Handle other errors (e.g., show a general error notification)
            setNotify({
              isOpen: true,
              message: 'An error occurred while fetching the report.',
              type: 'error'
            });
          }
        })
        .finally(() => {
          setLoading(false); // Stop loading regardless of success or failure
        });
    }
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center", padding: isEmbedded ? "10px 20px" : "30px", fontWeight: "500px" }}>
      <Paper elevation={0} sx={{ width: "1000px" }}>
        {!isEmbedded && (
          <Grid className="form-title" container spacing={2} justifyContent="center" style={{ marginTop: "15px" }} alignItems="center">
            <Grid item> <HiOutlineCurrencyRupee size={30} sx={{ width: 45, height: 45 }} /> </Grid>
            <Grid item> <Typography variant="h5">Combine Salary Report</Typography> </Grid>
          </Grid>
        )}
        {/* The form now just handles validation and triggers data fetching via the Display button's click handler */}
        <form className="classes.root" autoComplete="off" onSubmit={(e) => e.preventDefault()}>

          <Box sx={{ margin: isEmbedded ? "15px 30px" : "30px" }}>
            {/* All elements in one line */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              flexWrap: 'wrap'
            }}>

              {/* Month field */}
              <FormControl
                variant="outlined"
                sx={{ width: '200px' }}
                {...(errors.month && { error: true })}
              >
                <InputLabel><RequiredLabel label="Month" /></InputLabel>
                <Select
                  label="Month"
                  name="month"
                  size="small"
                  value={values.month}
                  onChange={handleInputChange}
                >
                  <MenuItem value="">None</MenuItem>
                  {
                    monthOptions.map(
                      item => (<MenuItem key={item.id} value={item.title}>{item.title}</MenuItem>)
                    )
                  }
                </Select>
                {errors.month && <FormHelperText>{errors.month}</FormHelperText>}
              </FormControl>

              {/* Year field */}
              <FormControl
                variant="outlined"
                sx={{ width: '200px' }}
                {...(errors.year && { error: true })}
              >
                <InputLabel><RequiredLabel label="Year" /></InputLabel>
                <Select
                  label="Year"
                  name="year"
                  size="small"
                  value={values.year}
                  onChange={handleInputChange}
                >
                  <MenuItem value="">None</MenuItem>
                  {
                    yearOptions.map(
                      item => (<MenuItem key={item.id} value={item.title}>{item.title}</MenuItem>)
                    )
                  }
                </Select>
                {errors.year && <FormHelperText>{errors.year}</FormHelperText>}
              </FormControl>

              {/* Buttons */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                {/* Removed Submit Button */}

                {/* Updated Display Button to trigger API call and show loading */}
                <Button
                  style={{ height: 40 }}
                  variant="outlined"
                  color="secondary"
                  onClick={handleDisplayClick} // Calls the function that handles API fetch and navigation
                  disabled={loading} // Disable while loading
                >
                  {loading ? (
                    <CircularProgress size={24} color="secondary" />
                  ) : (
                    'Display'
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        </form>

        <Popup
          title="Employee Form"
          openPopup={openPopup}
          setOpenPopup={setOpenPopup}
        >
          <DataTable />
        </Popup>

      </Paper>
      <Notification
        notify={notify}
        setNotify={setNotify}
      />
    </Box>
  )
}

export default CombineSalaryReport;